import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { accountAPI, transactionAPI } from "services";
import TxnsTView from "./TxnsTView";
import TxnsTableView from "./TxnsTableView";
import { HeaderShimmer, ShimmerLoader, TableShimmer, Loader } from "components";
import { debounce, formatMoney } from "utils";
import Header from "./Header";
import Footer from "./Footer";
import NoAccount from "./NoAccount";
import useUniversalStore from "store/universalStore";

const PublicKhata = () => {
  const { id } = useParams();

  const { selectedMonth } = useUniversalStore((state) => state);

  // State variables to hold various data
  const [txns, setTxns] = useState([]);  // Store transactions
  const [account, setAccount] = useState(null);  // Store account details
  const [openBalance, setOpenBalance] = useState(0);  // Store opening balance for the selected month
  const [loading, setLoading] = useState(true);  // State to manage loading state
  const [animation, setAnimation] = useState("");  // Animation class for the transaction view
  const [txnViewMode, setTxnViewMode] = useState(
    () => localStorage.getItem("publicKhataTxnView") || "t-view"
  );

  const handlesetTxnViewMode = (mode) => {
    setTxnViewMode(mode);
    localStorage.setItem("publicKhataTxnView", mode);
  };

  // Calculate monthly cash in/out locally
  const monthlyCash = useMemo(() => {
    let cashIn = 0;
    let cashOut = 0;

    txns.forEach(txn => {
      const isCancelled = typeof txn.get === "function" ? txn.get("cancelled") : txn.cancelled;
      if (!isCancelled) {
        const amount = (typeof txn.get === "function" ? txn.get("amount") : txn.amount) || 0;
        if (amount > 0) cashIn += amount;
        else cashOut += Math.abs(amount);
      }
    });

    return {
      in: formatMoney(cashIn),
      out: formatMoney(cashOut),
      totalDelta: cashIn - cashOut
    };
  }, [txns]);

  // Derive closing balance from opening balance and monthly delta
  const closeBalance = useMemo(() => {
    return openBalance + monthlyCash.totalDelta;
  }, [openBalance, monthlyCash.totalDelta]);

  const txnsWithBalance = useMemo(() => {
    let balance = openBalance;
    return (txns || []).map((txn) => {
      const isCancelled = txn.get("cancelled")
      const amount = txn.get("amount") || 0;
      if (!isCancelled) {
        balance += amount;
      }
      return { txn, balance };
    });
  }, [txns, openBalance]);

  // Consolidated and debounced data fetching
  const debouncedLoadAllData = useMemo(() => {
    const fetchFunc = async (mDate, accId) => {
      if (!accId || !mDate) return;
      try {
        const startDate = new Date(mDate.getFullYear(), mDate.getMonth(), 1, 0, 0, 0, 0);
        const prevMonthEnd = new Date(startDate.getTime() - 1);

        // Fetch everything in parallel
        const [accountResult, txnsResult, balanceResult] = await Promise.all([
          accountAPI.getAccountById(accId),
          transactionAPI.getTransactionsByAccountIdWithMonth(accId, mDate),
          transactionAPI.sumTillDateByAccount(prevMonthEnd, accId)
        ]);

        if (accountResult) setAccount(accountResult);
        setTxns(txnsResult);
        setOpenBalance(balanceResult);
      } catch (error) {
        console.error("Error loading Public Khata data:", error);
      } finally {
        setLoading(false);
      }
    };
    return debounce(fetchFunc, 800);
  }, []);

  // Fetch data when component is mounted or ID changes
  useEffect(() => {
    setLoading(true);
    debouncedLoadAllData(selectedMonth, id);
  }, [selectedMonth, id]);

  if (!account && !loading) return <NoAccount />;

  const shimmerView = () => {
    return (
      <div className="row p-0 m-0 w-100">
        <div className="col-6 col-md-6 list-group list-group-flush scrollbar-design h-100 px-3">
          <div className="shimmer-container">
            <HeaderShimmer />
            {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
          </div>
        </div>
        <div className="col-6 col-md-6 list-group list-group-flush scrollbar-design h-100 px-3">
          <div className="shimmer-container">
            <HeaderShimmer />
            {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header
        account={account}
        month={selectedMonth}
        setAnimation={setAnimation}
        txnViewMode={txnViewMode}
        setTxnViewMode={handlesetTxnViewMode}
      />

      <div className={`row p-0 m-0 background ${animation}`}
        onAnimationEnd={() => setAnimation("")}>
        {loading ? (
          txnViewMode === "table" ? (
            <TableShimmer
              isSharedAccount={true}
              showActions={false}
              height="calc(100vh - 175px)"
            />
          ) : (
            shimmerView()
          )
        ) : txnViewMode === "table" ? (
          <TxnsTableView
            txns={txnsWithBalance}
            openBalance={openBalance}
          />
        ) : (
          <TxnsTView
            txns={txns}
            monthlyCash={monthlyCash}
            openBalance={openBalance}
            closeBalance={closeBalance}
          />
        )}
      </div>
      <Footer id={id} account={account} />
    </>
  );
};

export default PublicKhata;

